import { Store } from "@reduxjs/toolkit";
import { t } from "ttag";

import * as addressAPI from "../../api/address";
import * as shippingAPI from "../../api/shipping";
import { updateEnteredLocation } from "../../store/common/reducers";
import { IRootState } from "../../store/reducers.interfaces";
import { HttpError } from "../../utils/ajax";
import { registerCascade } from "../../utils/cascade";
import { format } from "../../utils/format";
import {
    setDerivedFields,
    setFields,
    updateBillingStates,
    updateShippingMethods,
    updateShippingStates,
} from "./reducers";
import { IReduxState } from "./reducers.interfaces";
import { getCountryCode, getShippingAddress } from "./utils";

type IRootCheckoutState = Pick<IRootState, "checkout">;

// Extract the error messages from a failed shipping-methods lookup. The API
// returns `{ errors: [...] }` for real validation failures, but edge/WAF layers
// can instead return an opaque 403 (or a gateway 5xx) with a non-JSON body. In
// that case fall back to a generic message so the reducer never receives a
// missing errors array — which previously crashed checkout and left the user
// stuck with no message shown (TSI-TEMPURPEDIC-15HK).
const extractShippingMethodErrors = (error: unknown): string[] => {
    const body = error instanceof HttpError ? error.body : undefined;
    if (body && typeof body === "object" && "errors" in body) {
        const { errors } = body;
        if (
            Array.isArray(errors) &&
            errors.every((e): e is string => typeof e === "string")
        ) {
            return errors;
        }
    }
    return [
        t`We're having trouble loading shipping options. Please try again.`,
    ];
};

export const registerCheckoutCascades = (
    store: Store<IRootCheckoutState>,
): void => {
    // When the shipping ZIP code changes, update the global entered location
    registerCascade(store, {
        transformState: (storeState: IRootCheckoutState) => {
            return storeState.checkout;
        },

        getKey: (state) => {
            return `${state.form.shipping_line4} ${state.form.shipping_state} ${state.form.shipping_postcode}`;
        },

        doCascade: async (_, state) => {
            if (
                !state.form.shipping_postcode ||
                state.form.shipping_postcode.length < 5
            ) {
                return true;
            }
            const shortZip = state.form.shipping_postcode.slice(0, 5);
            store.dispatch(
                updateEnteredLocation({
                    formatted_address:
                        state.form.shipping_line4 && state.form.shipping_state
                            ? `${state.form.shipping_line4}, ${state.form.shipping_state}`
                            : shortZip,
                    formatted_address_long:
                        state.form.shipping_line1 &&
                        state.form.shipping_line4 &&
                        state.form.shipping_state &&
                        state.form.shipping_postcode
                            ? `${state.form.shipping_line1}, ${
                                  state.form.shipping_line4
                              }, ${
                                  state.form.shipping_state
                              } ${state.form.shipping_postcode.slice(0, 5)}`
                            : shortZip,
                    zip: shortZip,
                }),
            );
            return true;
        },
    });

    // Load shipping state choices when the shipping country changes
    registerCascade(store, {
        transformState: (storeState: IRootCheckoutState) => {
            return storeState.checkout;
        },

        getKey: (state) => {
            return getCountryCode(state.form.shipping_country);
        },

        doCascade: async (country) => {
            console.debug(`Cascade: shipping-states-${country}`);
            const states = await addressAPI.loadStates(country);
            store.dispatch(updateShippingStates(states));
            return true;
        },
    });

    // Load billing state choices when the billing country changes
    registerCascade(store, {
        transformState: (storeState: IRootCheckoutState) => {
            return storeState.checkout;
        },

        getKey: (state) => {
            return getCountryCode(state.form.billing_country);
        },

        doCascade: async (country) => {
            console.debug(`Cascade: billing-states-${country}`);
            const states = await addressAPI.loadStates(country);
            store.dispatch(updateBillingStates(states));
            return true;
        },
    });

    // Load Shipping Methods when shipping_country or shipping_state changes or
    // when basket lines change.
    registerCascade(store, {
        transformState: (storeState: IRootCheckoutState) => {
            return storeState.checkout;
        },

        getKey: (state) => {
            const numLines = state.data.basket
                ? state.data.basket.lines.length
                : 0;
            // Since shipping methods may depend on ZIP code, we need to update
            // them when ZIP code changes. But, we don't want to load the method
            // 5 times in the process of typing all 5 digits. So, only include
            // the ZIP in the refresh key once its at least 5 chars. And then,
            // only include the first 5 chars.
            const postcode = (
                state.form.shipping_postcode.length >= 5
                    ? state.form.shipping_postcode
                    : ""
            ).slice(0, 5);
            const voucherCodes = state.data.basket?.vouchers
                ?.map((voucher) => voucher.code)
                .join("#");
            return [
                getCountryCode(state.form.shipping_country),
                state.form.shipping_state,
                postcode,
                voucherCodes,
                `${numLines}`,
            ].join("-");
        },

        doCascade: async (key, state) => {
            console.debug(`Cascade: load-shipping-methods-${key}`);
            const shippingAddress = {
                ...getShippingAddress(state),
                country: getCountryCode(getShippingAddress(state).country),
            };
            if (
                !shippingAddress.country ||
                !shippingAddress.state ||
                !shippingAddress.postcode ||
                !state.data.basket ||
                state.data.basket.lines.length <= 0
            ) {
                return false;
            }
            try {
                const methods = await shippingAPI.loadMethods(shippingAddress);
                store.dispatch(updateShippingMethods({ methods, errors: [] }));
                // Detect the selected shipping method and re-select it in Redux
                let selectedMethodCode = "";
                if (methods.length > 0) {
                    const selectedMethod = methods.find((method) => {
                        return method.selected;
                    });
                    selectedMethodCode = selectedMethod
                        ? selectedMethod.code
                        : methods[0].code;
                }
                // Update the Redux state
                if (state.form.shipping_method !== selectedMethodCode) {
                    store.dispatch(
                        setFields({
                            shipping_method: selectedMethodCode,
                        }),
                    );
                }
            } catch (error) {
                console.error(error);
                store.dispatch(
                    updateShippingMethods({
                        methods: [],
                        errors: extractShippingMethodErrors(error),
                    }),
                );
            }
            return true;
        },
    });

    // Update shipping cost when cost changes
    const _findShippingMethod = (state: IReduxState) => {
        return state.data.shipping_methods.find((method) => {
            return state.form.shipping_method === method.code;
        });
    };
    registerCascade(store, {
        transformState: (storeState: IRootCheckoutState) => {
            return storeState.checkout;
        },

        getKey: (state) => {
            const method = _findShippingMethod(state);
            const price = method && method.price ? method.price.incl_tax : "";
            return `${state.form.shipping_method}-${price}`;
        },

        doCascade: async (key, state) => {
            console.debug(`Cascade: calculate-shipping-cost-${key}`);

            const method = _findShippingMethod(state);

            store.dispatch(
                setDerivedFields({
                    shipping_method_cost_excl_tax: format.decimal(
                        method ? method.price.excl_tax : "0.00",
                    ),
                    shipping_method_cost_incl_tax: format.decimal(
                        method ? method.price.incl_tax : "0.00",
                    ),
                    shipping_method_cost_before_discount_excl_tax:
                        format.decimal(
                            method
                                ? method.price_before_discount.excl_tax
                                : "0.00",
                        ),
                    shipping_method_cost_before_discount_incl_tax:
                        format.decimal(
                            method
                                ? method.price_before_discount.incl_tax
                                : "0.00",
                        ),
                }),
            );

            return true;
        },
    });
};
